import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useRestaurantAdminMutation } from 'src/apiClient';
import { useAppContext, useAppRouter } from 'src/appState';
import { LoadingButton } from 'elements/LoadingButton';
import { useTypedForm } from 'modules/form/typedForm';
import { muiTfInputs, typedFormSchemas } from 'modules/form/muiTypedFormInputs';
import { EmailSchema } from 'src/shared/types';

import type { NextPage } from 'next';

const useStyles = makeStyles((theme) => ({
    form: {
        // Fix IE 11 issue.
        marginTop: theme.spacing(1),
        width: '100%',
    },
    paper: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.spacing(8),
        padding: `${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(3)}`,
    },
}));

const LoginPage: NextPage = () => {
    const classes = useStyles();
    const { t } = useTranslation('restaurantAdmin.login');
    const { setAuth } = useAppContext();
    const { mutateAsync: login } = useRestaurantAdminMutation('POST', '/login');
    const { mutateAsync: getMyRestaurants } = useRestaurantAdminMutation('GET', '/restaurant');
    const { mutateAsync: resetPasswordRequest } = useRestaurantAdminMutation(
        'POST',
        '/restaurant/user/:email/reset-password',
    );

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { Form, Input, formState, getValues, setError } = useTypedForm({
        initialValues: {
            password: null,
            username: null,
        },
        onSubmit: async (values) => {
            closeSnackbar();

            const value = await login({
                data: values,
            });
            if (value === undefined) {
                return;
            }

            const restaurantsResult = await getMyRestaurants({
                headers: {
                    Authorization: `Bearer ${value.result.accessToken}`,
                },
            });
            if (restaurantsResult === undefined) {
                return;
            }

            const restaurants = restaurantsResult.result.sort((restaurantA, restaurantB) => restaurantA.name.localeCompare(restaurantB.name));
            setAuth({
                accessToken: value.result.accessToken,
                restaurants,
                user: value.result,
            }, restaurants[0] ?? null);
        },
        schema: {
            password: typedFormSchemas.nonEmptyString,
            username: typedFormSchemas.nonEmptyString255,
        },
    });

    const resetPassword = useCallback(async () => {
        const emailResult = EmailSchema.safeParse(getValues().username);
        if (!emailResult.success) {
            enqueueSnackbar(t('error.message.resetPassword'), { variant: 'error' });
            setError('username', {
                message: t('error.message.validEmail'),
            });
            return;
        }
        const response = await resetPasswordRequest({
            params: {
                email: emailResult.data,
            },
        });
        if (response === undefined) return;
        enqueueSnackbar(t('success.message.resetLink'), { variant: 'success' });
    }, [getValues, resetPasswordRequest, enqueueSnackbar, t, setError]);
    const router = useRouter();
    const appRouter = useAppRouter();
    return (
        <>
            <Container maxWidth="sm">
                <Paper className={classes.paper}>
                    <Box sx={{ pb: '2rem' }}>
                        <img alt="Qerko Logo" src={`${router.basePath}/qerko-logo.svg`} width={180} />
                    </Box>
                    <Typography component="h1" variant="h5">
                        {t('page.title.login')}
                    </Typography>
                    <Form className={classes.form}>
                        <Box my={4}>
                            <Input
                                Component={muiTfInputs.String}
                                inputProps={{
                                    autoComplete: 'username',
                                    fullWidth: true,
                                    label: t('input.label.loginName'),
                                }}
                                name="username"
                            />
                        </Box>

                        <Box my={4}>
                            <Input
                                Component={muiTfInputs.String}
                                inputProps={{
                                    autoComplete: 'current-password',
                                    fullWidth: true,
                                    label: t('input.label.password'),
                                    type: 'password' as const,
                                }}
                                name="password"
                            />
                        </Box>

                        <Box display="flex" flexDirection="column" justifyContent="center" width={1}>
                            <Box display="flex" justifyContent="center" my={2}>
                                <Box mr={3}>
                                    <LoadingButton
                                        buttonProps={{
                                            color: 'primary',
                                            fullWidth: true,
                                            size: 'large',
                                            type: 'submit',
                                            variant: 'contained',
                                        }}
                                        loading={formState.isSubmitting}
                                    >
                                        {t('button.title.submit')}
                                    </LoadingButton>
                                </Box>
                                <Box>
                                    <Button onClick={() => appRouter.pushRoute('/registration', {})} size="large" variant="text">
                                        {t('button.title.register')}
                                    </Button>
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="center">
                                <Button onClick={resetPassword} size="large" variant="text">{t('button.title.resetPassword')}</Button>
                            </Box>
                        </Box>
                    </Form>
                </Paper>
            </Container>
        </>
    );
};

export default LoginPage;
